/**
 * @fileoverview Event-surface query schemas — currently the `/events/discover`
 * (M7.2) tri-view query.
 *
 * The discover surface needs a single, well-typed query shape that the route
 * handler, facet aggregator, and (later) RTK Query bindings can all share. We
 * keep these in a separate file from the legacy event schemas in `schemas.ts`
 * so the surface is easy to evolve without churning the larger module.
 *
 * @author LOCI Web Team
 * @since WEB-52 (Discover · Tri-View Template — M7.2)
 */

import { z } from 'zod';

import { isGenreFilterKey } from '@loci/types';

// Canonical strict boolean-query coercion (API-1511 reuses it for `showAll`).
// Single source of truth in schemas.ts — see CodeRabbit dedup on #4163.
import { strictBooleanQuery } from './schemas';
import {
  DATE_WINDOW_KEYWORDS,
  type DateWindowFilter,
  type DateWindowKeyword,
  type RetiredDateWindowKeyword,
} from './dateWindows';

const blankToUndefined = (value: unknown) =>
  typeof value === 'string' && value.trim() === '' ? undefined : value;

/**
 * Coerce a query-string number while treating an empty string as
 * "not provided". `z.coerce.number()` would otherwise turn `''` into `0`,
 * which collapses optional numeric filters into noisy defaults.
 */
const optionalQueryNumber = (schema: z.ZodNumber) =>
  z.preprocess(blankToUndefined, schema.optional());

/**
 * Accept either a comma-separated string (`category=music,art`) or a repeated
 * query parameter (`?category=music&category=art`) and normalize to a unique
 * array of trimmed, non-empty strings. Returns `undefined` when no values
 * survive normalization so the caller can branch on `value !== undefined`.
 */
const commaOrRepeatedStringArray = z
  .union([z.array(z.string()), z.string()])
  .optional()
  .transform((value) => {
    if (value === undefined) return undefined;
    const parts = (Array.isArray(value) ? value : [value])
      .flatMap((s) => s.split(','))
      .map((s) => s.trim())
      .filter(Boolean);
    if (parts.length === 0) return undefined;
    return Array.from(new Set(parts));
  });

/**
 * Time-of-day buckets used to filter events by their start hour in the
 * **event's own timezone**. Bucketing happens in the route/service layer.
 *
 * - morning: `[5, 12)` local time
 * - afternoon: `[12, 17)` local time
 * - evening: `[17, 21)` local time
 * - night: `[21, 5)` local time (wraps midnight)
 */
export const discoverTimeOfDaySchema = z.enum(['morning', 'afternoon', 'evening', 'night']);
export type DiscoverTimeOfDay = z.infer<typeof discoverTimeOfDaySchema>;

const TIME_OF_DAY_VALUES = new Set(discoverTimeOfDaySchema.options);
const discoverTimeArraySchema = commaOrRepeatedStringArray.transform((value) => {
  if (value === undefined) return undefined;
  const allowed = value.filter((v): v is DiscoverTimeOfDay =>
    TIME_OF_DAY_VALUES.has(v as DiscoverTimeOfDay),
  );
  return allowed.length === 0 ? undefined : Array.from(new Set(allowed));
});

/**
 * Upper bound on how many genre keys one request may carry (WEB-832).
 *
 * Ticketmaster publishes genres on the order of tens, and the UI can only
 * select keys the facet actually returned, so this is far above any real
 * selection while keeping a hand-crafted request from handing the resolver an
 * unbounded key list to iterate and log.
 */
const MAX_GENRE_FILTER_KEYS = 50;

/**
 * `?genre=` — source-published genre KEYS, shape-validated at the server
 * boundary (WEB-832).
 *
 * Mirrors {@link discoverTimeArraySchema}: filter to what is actually
 * addressable, then normalize. Unlike `time` there is no closed vocabulary to
 * check against — the taxonomy belongs to the source and grows without a
 * release — so this validates SHAPE (`isGenreFilterKey`: lowercase kebab,
 * bounded length) and COUNT only, and leaves "is this a real genre?" to the
 * catalogue lookup in `resolveGenreFilterLabels`.
 *
 * The web client already applies the same guard on parse, but this schema is
 * the boundary that matters: `/events/discover` is a public GET, so a direct
 * request bypasses the client entirely. Malformed keys are dropped rather than
 * rejected, so a stale bookmark degrades to "no genre filter" instead of a 400
 * — matching how `time` and `vibe` treat values they cannot honour.
 */
const discoverGenreArraySchema = commaOrRepeatedStringArray.transform((value) => {
  if (value === undefined) return undefined;
  const allowed = value.filter(isGenreFilterKey).slice(0, MAX_GENRE_FILTER_KEYS);
  return allowed.length === 0 ? undefined : allowed;
});

// API-1817: the calendar view left the web in WEB-665 and nothing sends it, so
// `view=calendar` is no longer accepted — it fails this enum with a 400 rather
// than being silently served as a list.
export const discoverViewSchema = z.enum(['list', 'map']);
export type DiscoverView = z.infer<typeof discoverViewSchema>;

// Sort modes accepted on the wire (API-1821, ruling R1 of the Discovery PRD).
// Each one is a real order with a keyset cursor (see `discoverCursor.ts` in
// backend-api): `date` (soonest first, the default), `distance` (nearest to
// the request's spatial anchor) and `price` (lowest first). `recommended`,
// `relevance` and `popularity` were accepted as aliases of `date` and ranked
// nothing; they are gone, and a request that sends one gets a 400.
export const discoverSortSchema = z.enum(['date', 'distance', 'price']);
export type DiscoverSort = z.infer<typeof discoverSortSchema>;

/**
 * Named date keywords accepted by the `?date` query param: the windows
 * `dateWindows.ts` defines (API-1820), plus the retired `next-week` alias that
 * resolves to `next7`. The route handler resolves them through
 * `resolveDateWindow` in the city timezone.
 */
export const discoverDateKeywordSchema = z.enum([
  ...DATE_WINDOW_KEYWORDS,
  'next-week',
] as const satisfies readonly (DateWindowKeyword | RetiredDateWindowKeyword)[]);
export type DiscoverDateKeyword = z.infer<typeof discoverDateKeywordSchema>;

/**
 * The keywords a client may offer: {@link discoverDateKeywordSchema} without
 * the retired alias. The Locus `search_events` tool reads this one.
 */
export const offeredDiscoverDateKeywordSchema = z.enum(DATE_WINDOW_KEYWORDS);

const DATE_KEYWORD_VALUES = new Set<string>(discoverDateKeywordSchema.options);
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const ISO_DATE_RANGE_RE = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;

/**
 * Round-trip an `ISO_DATE_RE`-shaped string through `Date` to reject calendar-
 * invalid values like `2026-02-30` (the regex matches but the date doesn't
 * exist). Returns `true` only when the parsed UTC date renders back to the
 * exact same `YYYY-MM-DD` string.
 */
function isValidIsoCalendarDate(value: string): boolean {
  const parsed = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return false;
  return parsed.toISOString().slice(0, 10) === value;
}

/**
 * Parsed shape for the `date` filter. Either a named keyword OR an explicit
 * inclusive `[start, end]` range. Single ISO dates are normalized to a one-day
 * range with `end === start`.
 */
export type DiscoverDateFilter = DateWindowFilter;

const discoverDateSchema = z
  .string()
  .optional()
  .transform((value, ctx): DiscoverDateFilter | undefined => {
    if (value === undefined || value.trim() === '') return undefined;
    const trimmed = value.trim();

    if (DATE_KEYWORD_VALUES.has(trimmed)) {
      return { kind: 'keyword', value: trimmed as DiscoverDateKeyword };
    }

    const rangeMatch = trimmed.match(ISO_DATE_RANGE_RE);
    if (rangeMatch) {
      const [, start, end] = rangeMatch;
      if (!isValidIsoCalendarDate(start) || !isValidIsoCalendarDate(end)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'date range contains an invalid calendar date',
        });
        return z.NEVER;
      }
      if (start > end) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'date range end must be on or after start',
        });
        return z.NEVER;
      }
      return { kind: 'range', start, end };
    }

    if (ISO_DATE_RE.test(trimmed)) {
      if (!isValidIsoCalendarDate(trimmed)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'date is not a valid calendar date',
        });
        return z.NEVER;
      }
      return { kind: 'range', start: trimmed, end: trimmed };
    }

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'date must be a keyword (today, tomorrow, this-weekend, next7, next30, this-week) or an ISO date / range (YYYY-MM-DD or YYYY-MM-DD..YYYY-MM-DD)',
    });
    return z.NEVER;
  });

/** Radius used when `lat`/`lng` arrive without `radiusKm` (the Charlotte-MVP city radius). */
export const DEFAULT_DISCOVER_RADIUS_KM = 25;

/**
 * Query schema for `GET /api/v1/events/discover`.
 *
 * Notes on field naming:
 * - `lng` is used (not `lon`) to match the M1.2 `DiscoverEvent` view-model.
 * - `radiusKm` is in kilometers and only valid with `lat`/`lng`.
 * - `priceMin` / `priceMax` are in `currency` display units (dollars). The
 *   handler converts to integer cents internally before querying the DB.
 * - `category` and `neighborhood` accept comma-separated or repeated values.
 */
export const discoverQuerySchema = z
  .object({
    lat: optionalQueryNumber(z.coerce.number().min(-90).max(90)),
    lng: optionalQueryNumber(z.coerce.number().min(-180).max(180)),
    /**
     * Radius around `lat`/`lng`, in km. Only meaningful with an explicit
     * `lat`/`lng` pair (a "Search this area" viewport): sending it with a bare
     * `city` is a 400, because the city's own radius would silently win. No
     * schema default — the handler applies {@link DEFAULT_DISCOVER_RADIUS_KM}
     * when `lat`/`lng` arrive without one, so the refinement below can tell
     * an explicit radius apart from an omitted one.
     */
    radiusKm: optionalQueryNumber(z.coerce.number().positive().max(100)),

    /**
     * Lowercase city slug (e.g. `clt`). Binds the request to a city: the
     * neighborhood vocabulary and, when no `lat`/`lng` pair is sent, the
     * spatial anchor (the city's centroid + default radius from the
     * `@loci/city-editions` registry). A `lat`/`lng` pair sent alongside it
     * is a viewport ("Search this area") and moves the anchor inside the
     * city (API-1821). The web sends `city` on every Discover request.
     * Either `city` OR (`lat` + `lng`) MUST be supplied — see the
     * `superRefine` below.
     */
    city: z.string().trim().min(1).max(32).toLowerCase().optional(),

    date: discoverDateSchema,

    category: commaOrRepeatedStringArray,
    neighborhood: commaOrRepeatedStringArray,

    priceMin: optionalQueryNumber(z.coerce.number().nonnegative()),
    priceMax: optionalQueryNumber(z.coerce.number().nonnegative()),

    /**
     * WEB-1230 (ruling R6) — with `priceMin`/`priceMax` set, also return events
     * that have no listed price. A price bound never hides those events
     * silently: `facets.priceUnlisted` counts them, and this flag puts them
     * back. No effect without a price bound. "Off" is absence; the web never
     * sends `includeUnpriced=false`.
     */
    includeUnpriced: strictBooleanQuery,

    time: discoverTimeArraySchema,
    vibe: commaOrRepeatedStringArray,
    venue: commaOrRepeatedStringArray,

    /**
     * WEB-832 — source-published genre filter. Carries URL filter KEYS
     * (`genreFilterKey`: lowercase kebab, e.g. `hip-hop-rap`), not the
     * source's display label (`"Hip-Hop/Rap"`), which is what
     * `Event.sourceGenre` actually stores. The route resolves keys back to
     * stored labels against the catalogue before querying — nothing
     * un-slugifies, because the transform is lossy by design.
     *
     * Open vocabulary (unlike `vibe`): the values are whatever a source
     * published, so this schema validates shape only and the resolution step
     * is what rejects a genre nothing carries. An unknown key resolves to zero
     * labels and correctly returns an empty result — a filter that matches
     * nothing, never one that is silently ignored.
     *
     * Un-set is the important case: with no `genre` supplied the filter adds
     * NO predicate at all, so events whose `sourceGenre` is NULL — every
     * Eventbrite and UNC-Charlotte row, NULL by design — stay in the feed.
     *
     * Shape- and count-validated by {@link discoverGenreArraySchema} so a
     * direct API request cannot hand the resolver malformed or unbounded keys.
     */
    genre: discoverGenreArraySchema,

    q: z.preprocess(blankToUndefined, z.string().trim().min(1).max(200).optional()),

    view: discoverViewSchema.optional().default('list'),
    sort: discoverSortSchema.optional().default('date'),

    /**
     * Deep / "show everything" opt-in (API-1511). When `true`, the default
     * browse band floor is lifted so `LOW_CONFIDENCE` events resurface in the
     * deep results. Omitted / `false` keeps them out of the default body once
     * the quality gate (`LOCI_QUALITY_GATE_ENABLED`) is enabled; it has no
     * effect while the gate is OFF. Wire key is camelCase to match the rest of
     * the discover query (`priceMin`, `radiusKm`).
     */
    showAll: strictBooleanQuery,

    /**
     * "Only who I follow" opt-in (API-1664). When `true` on an **authenticated**
     * request the server resolves the viewer's own follow graph and narrows the
     * result set to events whose venue is followed OR whose lineup includes a
     * followed artist. Unlike `venue` (a client-supplied id list), the ids are
     * never accepted from the wire — the client cannot widen or forge them.
     *
     * Two behaviors are part of the contract and are asserted by test:
     * - **Anonymous** (`followedOnly=true`, no session): no-op. There is no
     *   follow graph to resolve, so the flag is ignored rather than 401'ing or
     *   returning an empty list for a viewer who never had a chance to follow.
     * - **Authenticated with an empty follow graph**: returns an empty set.
     *   Silently falling back to the unfiltered citywide list would make the
     *   toggle lie about what it did.
     *
     * The filter is applied inside `buildDiscoverWhere`, so facet counts are
     * narrowed by exactly the same constraint as the events list.
     */
    followedOnly: strictBooleanQuery,

    cursor: z.preprocess(blankToUndefined, z.string().min(1).max(512).optional()),
    // `limit=0` is a deliberate facets-only request from `useDiscoverFacetsQuery`
    // in `packages/api/src/endpoints/discover.ts` — it asks the handler to
    // short-circuit row materialization and return only facet counts. The
    // handler's page slice (`pageCandidates.slice(0, 0)`) naturally returns
    // an empty `events` array and a null cursor for this case.
    limit: z.coerce.number().int().min(0).max(100).default(50),
  })
  // API-1821 — strict params: an unknown key is a 400 (`unrecognized_keys`),
  // never silently dropped. A dropped key is how the MCP `dateRange` filter
  // shipped with no effect (API-446).
  .strict()
  .superRefine((data, ctx) => {
    const hasLat = data.lat !== undefined;
    const hasLng = data.lng !== undefined;
    const hasCity = data.city !== undefined;

    if (hasLat !== hasLng) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['lng'],
        message: 'Both lat and lng must be provided together',
      });
    }

    // API-1411: spatial anchor is now mandatory at the boundary. Either a
    // city slug (server-resolved to centroid + radius) OR an explicit
    // lat/lng pair must be supplied. Failing loud here replaces the
    // client-side fallback that silently produced cross-southeast queries.
    if (!hasCity && !(hasLat && hasLng)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['city'],
        message: 'city or (lat,lng) required',
      });
    }

    if (data.radiusKm !== undefined && !(hasLat && hasLng)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['radiusKm'],
        message: 'radiusKm requires lat and lng',
      });
    }

    if (
      data.priceMin !== undefined &&
      data.priceMax !== undefined &&
      data.priceMax < data.priceMin
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['priceMax'],
        message: 'priceMax must be >= priceMin',
      });
    }

    // `sort=distance` previously required explicit lat/lng. A `city` slug now
    // also satisfies the anchor requirement because the route handler resolves
    // it to a lat/lng before sort runs. City-home tonight (API-1737) may pass
    // either `city` or the edition centroid as `lat`/`lng`.
    if (data.sort === 'distance' && !hasCity && (!hasLat || !hasLng)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['sort'],
        message: 'sort=distance requires lat and lng',
      });
    }
  });

export type DiscoverQuery = z.infer<typeof discoverQuerySchema>;
